import { lazy } from "react";
import Live from "features/Live";
import AthleteHub from "components/AthleteHub/AthleteHub";
import CreateAthlete from "components/AthleteHub/CreateAthlete";
import EditAthlete from "components/AthleteHub/EditAthlete/EditAthlete";
import Groups from "features/Groups";
import CreateGroup from "components/Groups/CreateGroup/CreateGroup";
import Group from "features/Group";
import EditGroup from "components/Groups/EditGroup/EditGroup";
import Leaderboards from "features/Leaderboards";
import DataExport from "features/DataExport";
import Charting from "features/Charting";
import Workouts from "features/Workouts";
import AthleteProfile from "features/AthleteProfile";
import Settings from "features/Settings";
import NotFound from "features/NotFound";
import Constants from "config/Constants";
import { PRIVATE_ROUTE_TITLES } from "config/routes";
import { workoutReviewV2Enabled } from "config/features";
import { isSandboxRouteEnabled } from "config/sandbox";
import SandBox from "components/SandBox";
import useDocumentTitle from "hooks/useDocumentTitle";
import { Route } from "react-router-dom";
import { Routes } from "components/Sentry/Routes";
import useCanDo from "hooks/useCanDo";
import AthleteBulkUpload from "features/AthleteBulkUpload";
import ExerciseLibrary from "features/ExerciseLibrary";
import Dashboards from "features/Dashboards";
import Programming from "features/Programming";
import { UpsellGroupsAndMonitoring, UpsellOutputProgram } from "features/Upsell";

const CalendarRoutes = lazy(() => import("features/Calendar"));
const WorkoutReview = lazy(() => import("features/WorkoutReview"));

interface AppRoutesProps {}

function AppRoutes(props: AppRoutesProps) {
  const canDo = useCanDo();

  useDocumentTitle(PRIVATE_ROUTE_TITLES);

  return (
    <Routes>
      <Route path={`${Constants.routes.home.route}/*`} element={<Live />} />
      <Route path="/settings/*" element={<Settings />} />
      <Route path={Constants.routes.athletes.route} element={<AthleteHub />} />
      <Route path={Constants.routes.athletesAdd.route} element={<CreateAthlete />} />
      {canDo("athlete.bulkUpload") && (
        <>
          <Route path={Constants.routes.bulkUpload.route} element={<AthleteBulkUpload />} />
        </>
      )}
      <Route path={Constants.routes.athletesSingle.route} element={<AthleteProfile />} />
      <Route path={Constants.routes.athletesEdit.route} element={<EditAthlete />} />
      {workoutReviewV2Enabled && canDo("workout.any") && (
        <Route path={Constants.routes.workoutReview_V2.route} element={<WorkoutReview />} />
      )}
      {canDo("group.any") && (
        <>
          <Route path={Constants.routes.groups.route} element={<Groups />} />
          <Route path={Constants.routes.groupsAdd.route} element={<CreateGroup />} />
          <Route path={Constants.routes.groupsSingle.route} element={<Group />} />
          <Route path={Constants.routes.groupsEdit.route} element={<EditGroup />} />
        </>
      )}
      {canDo("leaderboards.any") && <Route path={Constants.routes.leaderboards.route} element={<Leaderboards />} />}
      {canDo("library.any") && (
        <Route path={`${Constants.routes.exerciseLibrary.route}/*`} element={<ExerciseLibrary />} />
      )}
      <Route path="/live" element={<Live />} />
      {canDo("charting.any") && <Route path="/charting" element={<Charting />} />}
      {canDo("dashboard.any") && <Route path="/dashboards/*" element={<Dashboards />} />}
      {canDo("export.any") && <Route path={Constants.routes.exportData.route} element={<DataExport />} />}
      {canDo("workout.any") && <Route path="workouts/*" element={<Workouts />} />}
      {canDo("programs.any") && <Route path="/programs/*" element={<Programming />} />}
      {canDo("calendar.any") && <Route path={`${Constants.routes.calendar.route}/*`} element={<CalendarRoutes />} />}
      <Route path={Constants.routes.upsellOutputProgram.route} element={<UpsellOutputProgram />} />
      <Route path={Constants.routes.upsellGroupsAndMonitoring.route} element={<UpsellGroupsAndMonitoring />} />
      {isSandboxRouteEnabled(window.location, process.env.NODE_ENV) && <Route path="/sandbox" element={<SandBox />} />}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default AppRoutes;
